import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTurnstile } from '@relay-towing/shared-website-components';
import { onboardingRequestBody, type OnboardingValues } from '@relay-towing/ui';
import { driverApi } from '../services/driverApi';
import { useSignIn } from './useSignIn';

/**
 * Creating an account from the phone, the dashboard's /onboarding over the
 * same route: `POST /onboarding/register` makes the Auth0 user, the
 * company and its owner (who also drives), then the new owner signs in
 * with the email they gave, as the dashboard's wizard ends.
 *
 * `turnstileRef` goes on an empty element on the last step: the bot check's
 * box, which solves itself while the step is open.
 */
export function useSignUp() {
  const turnstile = useTurnstile();
  const { signInAs } = useSignIn();

  const register = useMutation({
    mutationFn: async (values: OnboardingValues) => {
      const token = await turnstile.getToken();
      await driverApi.register(onboardingRequestBody(values), token);
      await signInAs(values.email);
    },
  });

  /** False when the email already has an account, which signs in instead. */
  const emailIsFree = useCallback(async (email: string) => {
    try {
      return (await driverApi.checkSignUpEmail(email)).available !== false;
    } catch {
      // The register route refuses a taken email too; a failed check is
      // not a reason to stop someone here.
      return true;
    }
  }, []);

  return {
    register,
    emailIsFree,
    turnstileRef: turnstile.containerRef,
  };
}
